import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {ConditionCardScratchpadState} from '../../scratchpad/conditionCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import updateFallbackOutputWithDefaultValues from '../../utils/conditions/updateFallbackOutputWithDefaultValues';
import {
  ConditionCategory,
  ConditionOperatorKey,
} from 'common/src/modules/conditions/conditions.types';
import {MacroItemTypeValue} from 'common/src/modules/macros/macros.types';
import {
  AiConditionOperator,
  fromAiCondOperator,
} from '../../cardDetailsV2/aiModels/cardDetailsAI.enums';

const setSignalInputSchema = z.object({
  signal: z
    .object({
      signalKey: z.string().describe('The signal key to set'),
      signalName: z.string().describe('The signal name to set'),
      signalType: z.nativeEnum(MacroItemTypeValue).describe('The signal type to set'),
      categories: z.array(z.nativeEnum(ConditionCategory)).describe('The categories to set'),
      optionalOperators: z
        .array(z.nativeEnum(AiConditionOperator))
        .describe('The supported operator names for this signal'),
      shouldQueryOptionalValues: z.boolean().describe('The should query optional values to set'),
    })
    .describe('The signal to set'),
});

export default function setSignalTool() {
  async function execute(
    params: ToolExecuteParams<typeof setSignalInputSchema, ConditionCardScratchpadState>,
  ) {
    const {signal} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<ConditionCardScratchpadState>(
      agentScratchpad,
      NodeType.CONDITION,
    );

    if (!validatedScratchpad.card.availableSignals?.some((s) => s.value === signal.signalKey)) {
      return toolError(
        `The signal ${signal.signalKey} is not valid for the condition card. Valid signals are: ${validatedScratchpad.card.availableSignals?.map((s) => s.value).join(', ')}`,
      );
    }

    validatedScratchpad.card.condKey = signal.signalKey;
    validatedScratchpad.card.condKeyType = signal.signalType;
    validatedScratchpad.card.categories = signal.categories;
    validatedScratchpad.card.signalDescription = signal.signalName;
    validatedScratchpad.card.optionalOperators = signal.optionalOperators
      .map((operatorName) => {
        const key = fromAiCondOperator(operatorName);

        return key !== undefined ? {value: key, title: operatorName as string} : undefined;
      })
      .filter((op): op is {value: ConditionOperatorKey; title: string} => op !== undefined);
    validatedScratchpad.card.shouldQueryOptionalValues = signal.shouldQueryOptionalValues;

    // Update fallback output with default values based on the new signal type
    const fallbackOutput = validatedScratchpad.card.outputs.find(
      (output) => output.type === OutputType.Fallback,
    );

    if (fallbackOutput) {
      updateFallbackOutputWithDefaultValues(
        fallbackOutput,
        signal.signalType,
        validatedScratchpad.card.condOperator,
      );
    }

    return toolResult(`Signal set successfully: ${JSON.stringify(signal)}`);
  }

  return tool<typeof setSignalInputSchema, ConditionCardScratchpadState>({
    name: V2CardAgentToolName.SetSignal,
    displayName: 'Setting signal',
    description:
      'Set the signal for the condition card. The signal must be one from the available search results.',
    inputSchema: setSignalInputSchema,
    execute,
  });
}
